import {
  DatePicker,
  Form,
  FormInstance,
  Input,
  Radio,
  Select,
  Tooltip,
} from 'antd';
import FormLabel from 'components/FormLabel';
import InformationBanner from 'components/InformationBanner';
import { requiredRule } from 'const/formRules';
import dayjs from 'dayjs';
import { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CategorieVehicule,
  CategorieVehiculeBus,
  CategorieVehiculeLourd,
  NouveauDossierConcessionnaireData,
  UsageVehiculeParticulier,
  VehiculeType,
  YesOrNo,
} from 'types/Concessionnaire';
import {
  M1_CONDITIONAL_QUESTION_NAMES,
  m1ConditionalQuestionRules,
  nbVehiculesRules,
  usageVehiculesLegerRules,
  yesNoQuestionValues,
} from 'views/dossiers/concessionnaire/steps/step2/step2.rules';
import {
  getUsageOptions,
  getVehiculeCategoryOptions,
  PERSONNE_PHYSIQUE_VUL_CATEGORY_LABELS,
  shouldShowFabricationEuropeQuestion,
  shouldShowM1ConditionalQuestions,
} from 'views/dossiers/concessionnaire/steps/step2/step2.selectors';

interface Step2QuestionsSectionFieldsProps {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  personnePhysique: boolean;
  vehiculeType?: VehiculeType;
  isVehiculeLourd: boolean;
  isBus: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
  usageVehicule?: UsageVehiculeParticulier;
}

export default function Step2QuestionsSectionFields({
  form,
  personnePhysique,
  vehiculeType,
  isVehiculeLourd,
  isBus,
  categorieVehicule,
  usageVehicule,
}: Step2QuestionsSectionFieldsProps) {
  const { t } = useTranslation();
  const single = personnePhysique ? '_single' : '';

  const usageOptions = getUsageOptions({
    personnePhysique,
    isVehiculeLourd,
    categorieVehicule,
  });
  const showM1ConditionalQuestions = shouldShowM1ConditionalQuestions({
    personnePhysique,
    categorieVehicule,
    usageVehicule,
  });
  const usagePlaceholder = t('nouveau_formulaire:usage_vehicules:placeholder');
  const showFabricationEuropeQuestion = shouldShowFabricationEuropeQuestion({
    personnePhysique,
    vehiculeType,
    categorieVehicule,
  });

  const getCategorieLabel = (
    categorie:
      | CategorieVehicule
      | CategorieVehiculeBus
      | CategorieVehiculeLourd,
  ) => {
    if (!personnePhysique && categorie === CategorieVehicule.M1) {
      return t(
        'nouveau_formulaire:categorie_vehicule:options:m1_personne_morale',
      );
    }

    if (
      personnePhysique &&
      categorie in PERSONNE_PHYSIQUE_VUL_CATEGORY_LABELS
    ) {
      return PERSONNE_PHYSIQUE_VUL_CATEGORY_LABELS[
        categorie as CategorieVehicule
      ];
    }

    return categorie;
  };

  const renderUsageOptionsByCategorie = () =>
    usageOptions.map((usage) => (
      <Select.Option key={usage} value={usage}>
        {usage}
      </Select.Option>
    ));

  const renderUsageVehiculesField = () => {
    if (personnePhysique) {
      if (!categorieVehicule) {
        return null;
      }

      return (
        <Form.Item<NouveauDossierConcessionnaireData>
          key={categorieVehicule}
          label={t('nouveau_formulaire:usage_vehicules:label_single')}
          name="usageVehiculeParticulier"
          rules={[requiredRule]}
        >
          <Select placeholder={usagePlaceholder}>
            {renderUsageOptionsByCategorie()}
          </Select>
        </Form.Item>
      );
    }

    if (isVehiculeLourd) {
      if (!categorieVehicule) {
        return null;
      }

      return (
        <Form.Item<NouveauDossierConcessionnaireData>
          key={categorieVehicule}
          label={t('nouveau_formulaire:usage_vehicules:label')}
          name="usageVehicules"
          rules={[requiredRule]}
        >
          <Select placeholder={usagePlaceholder}>
            {renderUsageOptionsByCategorie()}
          </Select>
        </Form.Item>
      );
    }

    return (
      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:usage_vehicules:label')}
        name="usageVehicules"
        rules={usageVehiculesLegerRules}
      >
        <Select placeholder={usagePlaceholder}>
          <Select.Option value="fonction">
            {t('nouveau_formulaire:usage_vehicules:options:fonction')}
          </Select.Option>
          <Select.Option value="location_courte">
            {t('nouveau_formulaire:usage_vehicules:options:location_courte')}
          </Select.Option>
          <Select.Option value="location_longue">
            {t('nouveau_formulaire:usage_vehicules:options:location_longue')}
          </Select.Option>
        </Select>
      </Form.Item>
    );
  };

  return (
    <>
      <Form.Item<NouveauDossierConcessionnaireData>
        label={
          !isBus ? (
            t('nouveau_formulaire:categorie_vehicule:label')
          ) : (
            <FormLabel>
              {t('nouveau_formulaire:categorie_vehicule:label')}
              <Tooltip
                overlayClassName="[&_.ant-tooltip-inner]:w-96"
                title={
                  <Trans
                    i18nKey="nouveau_formulaire:categorie_vehicule:bus_tip"
                    components={{
                      ul: <ul className="list-disc ml-4" />,
                      li: <li />,
                    }}
                  />
                }
              >
                <span className="absolute left-[100%] top-0">
                  <InformationBanner />
                </span>
              </Tooltip>
            </FormLabel>
          )
        }
        name="categorieVehicule"
        required
        rules={[requiredRule]}
      >
        <Select
          placeholder={t('nouveau_formulaire:categorie_vehicule:placeholder')}
          onChange={() => {
            form.setFieldsValue({
              usageVehiculeParticulier: undefined,
              produitEnEurope: undefined,
            });
          }}
        >
          {getVehiculeCategoryOptions({
            personnePhysique,
            isVehiculeLourdValue: isVehiculeLourd,
            isBusValue: isBus,
          }).map((categorie) => (
            <Select.Option key={categorie} value={categorie}>
              {getCategorieLabel(categorie)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {showFabricationEuropeQuestion && (
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:produitEnEurope:label')}
          name="produitEnEurope"
          required
          rules={[requiredRule]}
        >
          <Radio.Group className="[&_.ant-radio-label]:font-normal">
            <Radio value={YesOrNo.OUI}>{t('global:yes')}</Radio>
            <Radio value={YesOrNo.NON}>{t('global:no')}</Radio>
          </Radio.Group>
        </Form.Item>
      )}

      {personnePhysique ? null : (
        <Form.Item<NouveauDossierConcessionnaireData>
          rootClassName="[&_label]:!items-baseline"
          label={t('nouveau_formulaire:nb_vehicules:label')}
          name="nbVehicules"
          getValueFromEvent={(e: ChangeEvent<HTMLInputElement>) =>
            +e.target.value
          }
          required
          rules={nbVehiculesRules}
        >
          <Input
            placeholder={t('nouveau_formulaire:nb_vehicules:placeholder')}
            className="md:w-1/6"
            type="number"
            min={0}
            onKeyDown={(e) => {
              // Empeche l'incrementation avec les fleches du clavier et le pad/souris
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
              }
            }}
            onWheel={(e) => e.currentTarget.blur()}
          />
        </Form.Item>
      )}

      {renderUsageVehiculesField()}

      {showM1ConditionalQuestions &&
        M1_CONDITIONAL_QUESTION_NAMES.map((name) => (
          <Form.Item<NouveauDossierConcessionnaireData>
            key={name}
            label={t(`nouveau_formulaire:${name}:label`)}
            name={name}
            rules={m1ConditionalQuestionRules}
          >
            <Radio.Group className="[&_.ant-radio-label]:font-normal">
              <Radio value={yesNoQuestionValues[0]}>{t('global:yes')}</Radio>
              <Radio value={yesNoQuestionValues[1]}> {t('global:no')} </Radio>
            </Radio.Group>
          </Form.Item>
        ))}

      <Form.Item<NouveauDossierConcessionnaireData>
        label={
          <FormLabel>
            {t('nouveau_formulaire:date_livraison:label'.concat(single))}
            <Tooltip title={t('nouveau_formulaire:date_livraison:tip')}>
              <span className="absolute left-[100%] top-0">
                <InformationBanner />
              </span>
            </Tooltip>
          </FormLabel>
        }
        name="dateLivraison"
        required
        rules={[requiredRule]}
      >
        <DatePicker
          className="md:w-1/3"
          format="DD/MM/YYYY"
          placeholder={t('nouveau_formulaire:date_livraison:placeholder')}
          minDate={dayjs()}
        />
      </Form.Item>
    </>
  );
}
